import { Table, Tag, Typography } from "antd";

import DownloadLink from "@/components/ui/DowloadLink";
import ImageWithPreview from "@/components/ui/ImageWithPreview";
import ArrowTopRightIcon from "@/icons/ArrowTopRightIocn";

import { getIconByType } from "@/utils";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import LockCloseIcon from "@/icons/LockClosedIcon";
import CheckIcon from "@/icons/CheckIcon";
import PencilAltIcon from "@/icons/PencilAltIcon";
import ContextMenu from "@/components/containers/ContextMenu";
import ExpandableText from "@/components/views/ObjectFieldValue/ExpandableText";

const getString = (value) => value || "";

const sortString = (a, b) =>
  getString(a).localeCompare(getString(b), "ru", {
    sensitivity: "base",
    numeric: true,
  });

const getAuthorName = (author) =>
  [author?.lastname, author?.firstname, author?.patronymic]
    .filter(Boolean)
    .join(" ");

const formatFileSize = (size) => {
  const bytes = Number(size);

  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes === 0) return "0 Б";

  const units = ["Б", "КБ", "МБ", "ГБ", "ТБ"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;
  const fractionDigits = value >= 10 || unitIndex === 0 ? 0 : 1;

  return `${value.toLocaleString("ru-RU", {
    maximumFractionDigits: fractionDigits,
  })} ${units[unitIndex]}`;
};

const RelatedFilesTable = ({
  data = [],
  show_description = false,
  readOnly = true,
  loading = false,
  onDeleteItem,
}) => {
  const columns = [
    {
      title: <PencilAltIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
      dataIndex: "readOnly",
      key: "readOnly",
      width: 50,
      fixed: "left",
      align: "center",
      render: (readOnly) =>
        readOnly ? (
          <LockCloseIcon
            style={{
              fontSize: 18,
              color: "#BBBBBB",
              verticalAlign: "middle",
            }}
          />
        ) : (
          <CheckIcon
            style={{
              fontSize: 18,
              color: "#BBBBBB",
              verticalAlign: "middle",
            }}
          />
        ),
    },
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      width: 300,
      defaultSortOrder: "ascend",
      ellipsis: true,
      sorter: {
        compare: (a, b) =>
          sortString(
            `${a.name || ""}${a.extension ? `.${a.extension}` : ""}`,
            `${b.name || ""}${b.extension ? `.${b.extension}` : ""}`,
          ),
      },
      render: (name, record) => {
        const {
          type,
          extension,
          element_type,
          uuid,
          original_name,
          is_shortcut,
        } = record;

        const textNode = (
          <Typography.Text
            ellipsis={{
              tooltip: `${name || "—"}${extension ? `.${extension}` : ""}`,
            }}
          >
            {name || "—"}
            {extension ? `.${extension}` : ""}
          </Typography.Text>
        );

        const iconNode = (
          <div style={{ position: "relative", height: 22 }}>
            {is_shortcut && (
              <ArrowTopRightIcon
                style={{
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  fontSize: 12,
                  color: "var(--color-secondary)",
                  background: "#fff",
                  borderRadius: 4,
                }}
              />
            )}
            {getIconByType(type, extension, element_type)}
          </div>
        );

        if (type === "image") {
          return (
            <ImageWithPreview
              uuid={uuid}
              original_name={original_name}
              name={name}
              extension={extension}
              is_shortcut={is_shortcut}
            />
          );
        }

        return (
          <DownloadLink
            href={`/api/file/download?uuid=${uuid}`}
            original_name={original_name}
          >
            {iconNode}
            <div style={{ minWidth: 0, flex: 1 }}>{textNode}</div>
          </DownloadLink>
        );
      },
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
      width: 120,
      sorter: {
        compare: (a, b) =>
          sortString(
            a.element_type !== "folder" ? a.type : "",
            b.element_type !== "folder" ? b.type : "",
          ),
      },
      render: (type, { element_type }) =>
        element_type !== "folder" && type ? <Tag>{type}</Tag> : "—",
    },
    {
      title: "Размер",
      dataIndex: "size",
      key: "size",
      width: 110,
      render: (size, { element_type }) =>
        element_type !== "folder" ? formatFileSize(size) : "",
    },
    {
      title: "Рабочая группа",
      dataIndex: "unit",
      key: "unit",
      ellipsis: true,
      width: 170,
      sorter: {
        compare: (a, b) => sortString(a.unit?.name, b.unit?.name),
      },
      render: (_, item) => item.unit?.name || "—",
    },
    {
      title: "Автор",
      dataIndex: "author",
      key: "author",
      ellipsis: true,
      width: 250,
      sorter: {
        compare: (a, b) =>
          sortString(getAuthorName(a.author), getAuthorName(b.author)),
      },
      render: (author) => <EmployeeProfile noAvatar {...author} />,
    },
    show_description
      ? {
          title: "Описание",
          dataIndex: "description",
          key: "description",
          width: 250,
          sorter: {
            compare: (a, b) => sortString(a.description, b.description),
          },
          render: (description) => (
            <ExpandableText value={description || ""} line={2} />
          ),
        }
      : null,
    !readOnly
      ? {
          title: "",
          align: "center",
          width: 43,
          fixed: "right",
          key: "actions",
          render: (_, item) =>
            item.element_type !== "folder" &&
            !item.readOnly && (
              <ContextMenu vertical onDelete={() => onDeleteItem(item)} />
            ),
        }
      : null,
  ].filter(Boolean);

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="uuid"
      pagination={false}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      loading={loading}
      size="small"
      scroll={{ x: "100%" }}
      tableLayout="fixed"
      bordered
    />
  );
};

export default RelatedFilesTable;
